<div class="p-5 max-w-6xl mx-auto">
  <div class="flex justify-between items-center mb-5">
  <h1 class="text-2xl font-medium">{{ water() ? ('water-services.form.edit-title' | translate: { waterTitle: water()?.title }) : ('water-services.form.loading' | translate) }}</h1>
    <div class="flex gap-2">
      @if (saving()) {
        <mat-spinner diameter="30" />
      }
      <button matButton (click)="cancel()">{{ 'water-services.form.cancel' | translate }}</button>
      <button
        matButton="filled"
        (click)="save()"
        [disabled]="saving()"
        class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2">
        {{ 'water-services.form.save' | translate }}
      </button>
    </div>
  </div>

  <!-- AI Translation Section -->
  @if (!loading() && services.length > 0) {
    <mat-card class="mb-6">
      <mat-card-content>
        <div class="flex items-center gap-4 flex-wrap">
          <h3 class="text-lg font-medium">{{ 'water-services.form.translate-title' | translate }}</h3>

          <button
            matButton="filled"
            (click)="translateMissing()"
            [disabled]="translating()"
            class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2 ml-auto">
            @if (translating()) {
              <mat-spinner diameter="20" />
            } @else {
              <mat-icon>translate</mat-icon>
            }
            {{ 'water-services.form.translate-missing-button' | translate }}
          </button>
        </div>
        <p class="text-sm text-gray-600 mt-2">{{ 'water-services.form.translate-missing-hint' | translate }}</p>
      </mat-card-content>
    </mat-card>
  }

  @if (loading()) {
    <div class="flex justify-center items-center min-h-[300px]">
      <mat-spinner />
    </div>
  } @else {
    <form [formGroup]="form">
      @if (services.length > 1) {
        <p class="text-sm text-gray-600 mb-3">{{ 'water-services.form.reorder-hint' | translate }}</p>
      }

      <div class="flex flex-col gap-4" cdkDropList (cdkDropListDropped)="drop($event)">
        @for (service of services.controls; track service; let i = $index, count = $count) {
          <mat-card class="pt-4" cdkDrag [cdkDragDisabled]="count === 1">
            <!-- Compact drag preview; a full service card is too tall to drag around -->
            <div *cdkDragPreview class="flex items-center gap-2 rounded bg-white px-4 py-3 text-gray-800">
              <mat-icon class="text-gray-500">drag_indicator</mat-icon>
              <span class="font-medium">{{ getServiceLabel(asFormGroup(service)) }}</span>
            </div>

            <!-- Compact placeholder so the whole list stays visible while dragging -->
            <div *cdkDragPlaceholder class="h-16"></div>

            <!-- Reorder header: drag handle, position and arrow buttons -->
            <div class="flex items-center gap-2 px-4 pb-2 border-b border-gray-200">
              <mat-icon
                class="text-gray-500 shrink-0"
                [class.cursor-move]="count > 1"
                [matTooltip]="'water-services.form.drag-hint' | translate"
                cdkDragHandle>
                drag_indicator
              </mat-icon>
              <span class="text-sm text-gray-500 shrink-0">{{ i + 1 }}.</span>
              <span class="font-medium truncate min-w-0">{{ getServiceLabel(asFormGroup(service)) }}</span>

              <div class="ml-auto flex shrink-0">
                <button
                  matIconButton
                  type="button"
                  (click)="moveUp(i)"
                  [disabled]="i === 0"
                  [matTooltip]="'water-services.form.move-up' | translate"
                  [attr.aria-label]="'water-services.form.move-up' | translate">
                  <mat-icon>arrow_upward</mat-icon>
                </button>
                <button
                  matIconButton
                  type="button"
                  (click)="moveDown(i)"
                  [disabled]="i === count - 1"
                  [matTooltip]="'water-services.form.move-down' | translate"
                  [attr.aria-label]="'water-services.form.move-down' | translate">
                  <mat-icon>arrow_downward</mat-icon>
                </button>
              </div>
            </div>

            <mat-card-content [formGroup]="asFormGroup(service)">
              <!-- Language Tabs -->
              <mat-tab-group [selectedIndex]="activeLocaleIndex()" (selectedIndexChange)="activeLocale.set(locales[$event].code)" class="mb-6">
                @for (locale of locales; track locale.code) {
                  <mat-tab>
                    <ng-template mat-tab-label>
                      <span [class.text-red-600]="hasLocaleErrors(locale.code)" [class.font-bold]="hasLocaleErrors(locale.code)" class="flex items-center gap-1">
                        {{ locale.label }}
                        @if (hasLocaleErrors(locale.code)) {
                          <mat-icon [style.color]="'#dc2626'" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">error</mat-icon>
                        } @else if (locale.code !== 'Nl' && hasLocaleEmptyValues(asFormGroup(service), locale.code)) {
                          <mat-icon [style.color]="'#f59e0b'" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">warning</mat-icon>
                        }
                        @if (locale.code === 'En' || locale.code === 'Nl') {
                          <span class="text-xs">*</span>
                        }
                      </span>
                    </ng-template>

                    <!-- Tab content for each locale -->
                    <div class="p-4">
                      <div class="flex flex-col gap-4">
                        <!-- Title -->
                        <mat-form-field class="w-full">
                          <mat-label>{{ 'water-services.form.title-label' | translate }} <span class="text-xs text-gray-500">{{ getNlReference(asFormGroup(service), locale.code) }}</span></mat-label>
                          <input
                            matInput
                            [formControlName]="getTitleControlName(locale.code)"
                            maxlength="200" />
                          @if (service.get(getTitleControlName(locale.code))?.hasError('required')) {
                            <mat-error>{{ 'water-services.form.title-required' | translate }}</mat-error>
                          }
                          @if (service.get(getTitleControlName(locale.code))?.hasError('maxlength')) {
                            <mat-error>{{ 'water-services.form.title-too-long' | translate }}</mat-error>
                          }
                        </mat-form-field>

                        <!-- Unit -->
                        <mat-form-field class="w-full">
                          <mat-label>{{ 'water-services.form.unit-label' | translate }} <span class="text-xs text-gray-500">{{ getNlUnitReference(asFormGroup(service), locale.code) }}</span></mat-label>
                          <input
                            matInput
                            [formControlName]="getUnitControlName(locale.code)"
                            maxlength="50" />
                          <mat-hint>{{ 'water-services.form.unit-hint' | translate }}</mat-hint>
                          @if (service.get(getUnitControlName(locale.code))?.hasError('required')) {
                            <mat-error>{{ 'water-services.form.unit-required' | translate }}</mat-error>
                          }
                        </mat-form-field>
                      </div>
                    </div>
                  </mat-tab>
                }
              </mat-tab-group>

              <!-- Shared fields (Image and Price) -->
              <div class="grid grid-cols-[200px_1fr] gap-8 items-start mt-6">
                <!-- Image Upload (shared across all languages) -->
                <div class="flex flex-col gap-2">
                  <label class="text-xs text-gray-600 font-medium">{{ 'water-services.form.image-label' | translate }}</label>
                  @if (getImageUrl(asFormGroup(service))) {
                    <div class="relative w-[200px] h-[200px] rounded overflow-hidden border border-gray-200">
                      <img [src]="getImageUrl(asFormGroup(service))" alt="Service image" class="w-full h-full object-cover" />
                      <button
                        matMiniFab
                        class="red-button !absolute top-1 right-1 bg-white/90"
                        (click)="removeImage(i)"
                        [matTooltip]="'water-services.form.remove-image' | translate"
                        type="button">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  } @else {
                    <div class="w-[200px] h-[200px] border-2 border-dashed border-gray-200 rounded flex items-center justify-center">
                      <input
                        type="file"
                        accept="image/*"
                        (change)="onFileSelected($event, i)"
                        #fileInput
                        style="display: none" />
                      <button
                        mat-raised-button
                        type="button"
                        (click)="fileInput.click()">
                        <mat-icon>upload</mat-icon>
                        {{ 'water-services.form.upload-image' | translate }}
                      </button>
                    </div>
                  }
                  <span class="text-xs text-gray-500">{{ 'water-services.form.image-hint' | translate }}</span>
                </div>

                <!-- Price (shared across all languages) -->
                <mat-form-field class="w-full">
                  <mat-label>{{ 'water-services.form.price-label' | translate }}</mat-label>
                  <input
                    matInput
                    type="number"
                    formControlName="pricePerUnit"
                    required
                    min="0"
                    step="0.01" />
                  <span matTextPrefix>€&nbsp;</span>
                  @if (service.get('pricePerUnit')?.hasError('required')) {
                    <mat-error>{{ 'water-services.form.price-required' | translate }}</mat-error>
                  }
                  @if (service.get('pricePerUnit')?.hasError('min')) {
                    <mat-error>{{ 'water-services.form.price-min' | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>
            </mat-card-content>

            <mat-card-actions align="end">
              <button
                matIconButton
                class="red-button"
                (click)="removeService(i)"
                type="button"
                matTooltip="{{ 'water-services.form.remove-service' | translate }}">
                <mat-icon>delete</mat-icon>
              </button>
            </mat-card-actions>
          </mat-card>
        }
      </div>

      <div class="mt-8 flex justify-center">
        <button
          matButton="elevated"
          (click)="addService()"
          type="button"
          class="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 flex items-center gap-2">
          <mat-icon>add</mat-icon>
          {{ 'water-services.form.add-service' | translate }}
        </button>
      </div>
    </form>
  }
</div>
